/**
 * The wire's shared scalar formats (D37; the 2026-09-27 review §1.3): ids, timestamps, hashes and link states,
 * declared once. Before this module one concept was declared up to eight times with different constraints (a
 * package hash four ways, under two exports of the same name; a node id eight ways; twelve private ISO copies), and
 * the compatibility baseline would have frozen every one of them.
 *
 * **Choosing a constraint** (docs/compatibility.md §4.2). A request field keeps the strictest form every current
 * client satisfies: the server validates what it accepts. A response, frame or stored field takes the loosest form
 * any live writer emits, so nothing a current writer sends is refused — a reader tighter than a writer breaks on
 * older data, and loosening it after the baseline breaks older readers. Hence the pairs below: the form a value is
 * MINTED in (`nodeIdWireSchema`, `packageHashWireSchema`) and the bounded form a reader accepts it in
 * (`boundedIdWireSchema`, `recordedPackageHashWireSchema`).
 *
 * The domain's formats (`ulidSchema` — production, rundown and row ids —, `hlcSchema`, …) stay in
 * `@acp/domain/primitives.js`, a scalar module wire schemas may use (D37's naming rule); this module is for the formats
 * only the wire has.
 */
import { z } from 'zod';
import { openEnum } from './openEnum.js';

// ─── time ───

/** An ISO-8601 timestamp with an offset (`2026-09-27T10:00:00.000Z`). */
export const isoTimestampWireSchema = z.iso.datetime({ offset: true });

/** Milliseconds since the Unix epoch, stamped by the sender. */
export const epochMsWireSchema = z.number().int().nonnegative();

// ─── ids ───

/**
 * A tenant id is chosen by the vendor at creation and never changes. It is a
 * key segment (`T#<tenantId>#…`), hence the strict alphabet.
 */
export const tenantIdWireSchema = z
  .string()
  .regex(/^[a-z][a-z0-9-]{2,39}$/, 'tenant ids are 3-40 lowercase letters, digits or hyphens, starting with a letter');

/** Group, site and studio ids: a key segment, so no `#` and no whitespace. */
export const entityIdWireSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/, 'ids are 1-64 letters, digits, hyphens or underscores');

/** A Cognito `sub`: a UUID. */
export const userIdWireSchema = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, 'user ids are Cognito subs (UUIDs)');

/** A studio id: the same alphabet a group's `scope.studioIds` holds (D14), so a grant can name it. */
export const studioIdWireSchema = entityIdWireSchema;
/** A site id (the automatic cloud site's is `cloud`). */
export const siteIdWireSchema = entityIdWireSchema;
/** A show style id. */
export const showStyleIdWireSchema = entityIdWireSchema;

/** A node id as the cloud mints it at enrolment: a ULID, so node ids are unique across tenants and sort by enrolment time. */
export const nodeIdWireSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'node ids are ULIDs');

/**
 * An id a reader receives and does not re-check the minting format of: 1-128 characters. The node ids a pair, a
 * witness, the health report and the cloud's listings carry are in this form — an unenrolled node (dev, a LAN-only
 * pair, the test suites) names itself, and a reader has no business refusing its report over the spelling — and so
 * are user, device and connection ids on frames.
 */
export const boundedIdWireSchema = z.string().min(1).max(128);

/** A site-link message id, and the request ids a request/answer pair over it shares. */
export const messageIdWireSchema = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);

// ─── hashes ───

/** A SHA-256 hash, lower-case hex: a release list's `listHash`, a package in it. */
export const sha256HexWireSchema = z.string().regex(/^[0-9a-f]{64}$/, 'a SHA-256 hash is 64 lowercase hex characters');

/** A production package's content hash (D15, PLAN §4.2) as it is minted and named: its SHA-256. */
export const packageHashWireSchema = sha256HexWireSchema;

/**
 * The package hash a production RECORD carries (`packageHash`, PLAN §4.1), as stored and served: 8-128 lower-case
 * hex. Looser than `packageHashWireSchema` because records predate the release format (phase 3 accepted any
 * 8-128 hex digest) and a stored record must still read; every writer since pins a SHA-256.
 */
export const recordedPackageHashWireSchema = z.string().regex(/^[0-9a-f]{8,128}$/, 'a package hash is 8-128 lowercase hex characters');

// ─── link states ───

/**
 * One link-state vocabulary (the review found five declarations and two vocabularies): a link is `connected` or
 * `disconnected`, and each observer has one extra value for "there is no link to speak of", named from where it
 * stands — the cloud's `never-connected` (it has never heard from the site), the node's `not-configured` (it has no
 * cloud). The two are kept apart rather than renamed into one: renaming either on the wire would break every reader
 * in the field.
 */
export const LINK_STATES = ['connected', 'disconnected'] as const;

/** A peer link (a node's to its pair's other node). Open: a reader receives it from another node or the cloud. */
export const peerLinkStateWireSchema = openEnum(LINK_STATES);
export type PeerLinkStateWire = z.infer<typeof peerLinkStateWireSchema>;

/** The site link as the cloud sees it (PLAN §10.2): sites, `GET /status`, the vendor console. Open. */
export const siteLinkStateWireSchema = openEnum([...LINK_STATES, 'never-connected']);

/**
 * The node's own link to its cloud, as the node emits it (`/health`, `/local/auth`). Closed on the writer's side;
 * a reader maps a value it does not know to `disconnected` at its boundary (`cloudLinkStateReadWireSchema`): as far
 * as it can tell, not connected — which is what decides offline sign-in and failover.
 */
export const cloudLinkStateWireSchema = z.enum([...LINK_STATES, 'not-configured']);
export type CloudLinkStateWire = z.infer<typeof cloudLinkStateWireSchema>;
/** `cloudLinkStateWireSchema` as a reader parses it: an unknown value is `disconnected`. */
export const cloudLinkStateReadWireSchema = cloudLinkStateWireSchema.catch('disconnected');

